import React, { useEffect, useState } from 'react'
import { BookOpen } from 'lucide-react'
import { Avatar } from '@astryxdesign/core/Avatar'
import { HStack, VStack } from '@astryxdesign/core/Layout'
import { Link } from '@astryxdesign/core/Link'
import { Tab, TabList } from '@astryxdesign/core/TabList'
import { Text } from '@astryxdesign/core/Text'
import { Timestamp } from '@astryxdesign/core/Timestamp'
import LandingHeader from './LandingHeader'
import PublishedStoryCard from './PublishedStoryCard'
import { publishedStoryUrl } from './publishedStoryLinks'
import SettingsPanel, { type SettingsSection } from './SettingsPanel'
import StarRating from './StarRating'
import { authClient } from '../lib/auth-client'
import type { AppSettings } from '../App'

type ProfileTab = 'published' | 'library' | 'reviews'

interface AuthorProfileViewProps {
  profileId: string
  tab: ProfileTab
}

/**
 * The public page for one author profile: what they published, what they saved, what they
 * reviewed.
 *
 * Each tab loads on first visit rather than all three upfront. The library tab may legitimately
 * 403 when its owner has hidden it, and a rejected request during page load would read as a
 * broken page rather than as a private list.
 */
export default function AuthorProfileView({
  profileId,
  tab
}: AuthorProfileViewProps): React.JSX.Element {
  const { data: session } = authClient.useSession()
  const [author, setAuthor] = useState<AuthorProfilePage | null>(null)
  const [activeTab, setActiveTab] = useState<ProfileTab>(tab)
  const [published, setPublished] = useState<PublishedStorySummary[] | null>(null)
  const [library, setLibrary] = useState<PublishedStorySummary[] | null>(null)
  const [reviews, setReviews] = useState<AuthorReview[] | null>(null)
  const [libraryError, setLibraryError] = useState('')
  const [error, setError] = useState('')

  const [authorProfile, setAuthorProfile] = useState<AuthorProfile | null>(null)
  const [isProfileLoaded, setIsProfileLoaded] = useState(false)
  const [settings, setSettings] = useState<AppSettings | null>(null)
  const [settingsSection, setSettingsSection] = useState<SettingsSection>('account')
  const [showSettings, setShowSettings] = useState(false)

  useEffect(() => {
    let active = true
    setAuthor(null)
    setError('')
    window.api.authors
      .get(profileId)
      .then((loaded) => {
        if (active) setAuthor(loaded)
      })
      .catch((reason) => {
        if (active) setError(reason instanceof Error ? reason.message : 'Author not found.')
      })
    return () => {
      active = false
    }
  }, [profileId])

  useEffect(() => {
    let active = true
    if (activeTab === 'published' && published === null) {
      void window.api.authors
        .published(profileId)
        .then((items) => {
          if (active) setPublished(items)
        })
        .catch(() => {
          if (active) setPublished([])
        })
    }
    if (activeTab === 'library' && library === null) {
      void window.api.authors
        .library(profileId)
        .then((items) => {
          if (active) setLibrary(items)
        })
        .catch((reason) => {
          if (!active) return
          setLibrary([])
          setLibraryError(reason instanceof Error ? reason.message : 'This library is private.')
        })
    }
    if (activeTab === 'reviews' && reviews === null) {
      void window.api.authors
        .reviews(profileId)
        .then((items) => {
          if (active) setReviews(items)
        })
        .catch(() => {
          if (active) setReviews([])
        })
    }
    return () => {
      active = false
    }
  }, [activeTab, profileId, published, library, reviews])

  const selectTab = (next: string): void => {
    const value = next as ProfileTab
    setActiveTab(value)
    const path = `/profiles/${encodeURIComponent(profileId)}?tab=${value}`
    if (window.location.pathname + window.location.search !== path) {
      window.history.pushState(null, '', path)
    }
  }

  useEffect(() => {
    if (!session?.user) return
    let active = true
    Promise.all([window.api.profiles.getActive(), window.api.settings.get()])
      .then(([profile, loadedSettings]) => {
        if (!active) return
        setAuthorProfile(profile)
        setSettings(loadedSettings)
      })
      .catch(() => undefined)
      .finally(() => {
        if (active) setIsProfileLoaded(true)
      })
    return () => {
      active = false
    }
  }, [session?.user])

  const openSettings = (section: 'account' | 'profile'): void => {
    setSettingsSection(section)
    void window.api.settings
      .get()
      .then((latestSettings) => {
        setSettings(latestSettings)
        setShowSettings(true)
      })
      .catch(() => setShowSettings(true))
  }

  const saveSettings = (updatedSettings: AppSettings): void => {
    setSettings(updatedSettings)
    localStorage.setItem('ninenights-settings', JSON.stringify(updatedSettings))
    void window.api.settings.save(updatedSettings)
  }

  const syncTheme = (theme: AppSettings['theme']): void => {
    setSettings((current) => (current ? { ...current, theme } : current))
  }

  const saveProfile = async (changes: {
    penName: string
    authorBlurb: string
    libraryPublic: boolean
  }): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.update(authorProfile.id, changes)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const uploadAvatar = async (file: File): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.uploadAvatar(authorProfile.id, file)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const removeAvatar = async (): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.removeAvatar(authorProfile.id)
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const selectDefaultAvatar = async (defaultAvatarId: string): Promise<AuthorProfile> => {
    if (!authorProfile) throw new Error('Your author profile is not available.')
    const updatedProfile = await window.api.profiles.selectDefaultAvatar(
      authorProfile.id,
      defaultAvatarId
    )
    setAuthorProfile(updatedProfile)
    return updatedProfile
  }

  const accountDisplayName =
    authorProfile?.penName.trim() || session?.user?.name?.trim() || undefined
  const isAccountLoading = Boolean(session?.user) && !isProfileLoaded

  const header = (
    <LandingHeader
      showNavLinks={false}
      showAppButton={false}
      isAppHeader
      isAccountLoading={isAccountLoading}
      userName={accountDisplayName}
      avatarUrl={authorProfile?.avatarUrl}
      onOpenSettings={openSettings}
      currentTheme={settings?.theme}
      onThemeChange={syncTheme}
    />
  )

  const settingsModal = showSettings && settings && (
    <SettingsPanel
      settings={settings}
      profile={authorProfile}
      initialSection={settingsSection}
      onSaveSettings={saveSettings}
      onSaveProfile={saveProfile}
      onUploadAvatar={uploadAvatar}
      onRemoveAvatar={removeAvatar}
      onSelectDefaultAvatar={selectDefaultAvatar}
      onClose={() => setShowSettings(false)}
    />
  )

  if (error) {
    return (
      <main className="published-story-shell">
        {header}
        <div className="published-story-state">
          <BookOpen size={32} />
          <h1>Author unavailable</h1>
          <p>{error}</p>
        </div>
        {settingsModal}
      </main>
    )
  }

  if (!author) {
    return (
      <main className="published-story-shell">
        {header}
        <div className="published-story-state">
          <p>Loading profile…</p>
        </div>
        {settingsModal}
      </main>
    )
  }

  return (
    <main className="published-story-shell all-stories-page-shell">
      {header}
      <section className="all-stories-page">
        <VStack gap={6}>
          <HStack gap={5} align="center" wrap="wrap">
            <Avatar size='xl' name={author.penName} src={author.avatarUrl ?? undefined} />
            <VStack gap={1}>
              <h1>{author.penName}</h1>
              {author.authorBlurb && <Text color="secondary">{author.authorBlurb}</Text>}
              <Text type="supporting">
                Joined <Timestamp value={Math.floor(author.joinedAt / 1000)} format="date" />
              </Text>
            </VStack>
          </HStack>

          <TabList value={activeTab} onChange={selectTab} hasDivider>
            <Tab value="published" label={`Published (${author.counts.published})`} />
            <Tab value="library" label={`Library (${author.counts.library})`} />
            <Tab value="reviews" label={`Reviews (${author.counts.reviews})`} />
          </TabList>

          {activeTab === 'published' &&
            (published === null ? (
              <Text type="supporting">Loading stories…</Text>
            ) : published.length === 0 ? (
              <Text type="supporting">{author.penName} has not published anything yet.</Text>
            ) : (
              <section className="home-feed-list">
                {published.map((story, index) => (
                  <PublishedStoryCard key={story.id} story={story} index={index} />
                ))}
              </section>
            ))}

          {activeTab === 'library' &&
            (library === null ? (
              <Text type="supporting">Loading library…</Text>
            ) : libraryError ? (
              <Text type="supporting">{libraryError}</Text>
            ) : library.length === 0 ? (
              <Text type="supporting">No saved stories yet.</Text>
            ) : (
              <section className="home-feed-list">
                {library.map((story, index) => (
                  <PublishedStoryCard key={story.id} story={story} index={index} />
                ))}
              </section>
            ))}

          {activeTab === 'reviews' &&
            (reviews === null ? (
              <Text type="supporting">Loading reviews…</Text>
            ) : reviews.length === 0 ? (
              <Text type="supporting">No reviews written yet.</Text>
            ) : (
              <VStack gap={4}>
                {reviews.map((item) => (
                  <article key={item.id} className="author-review">
                    <VStack gap={2}>
                      <HStack gap={2} align="center" wrap="wrap">
                        <StarRating value={item.stars} size={14} />
                        <Link href={publishedStoryUrl(item.story)}>{item.story.title}</Link>
                        <Text type="supporting">by {item.story.authorName}</Text>
                      </HStack>
                      <Text>{item.review}</Text>
                    </VStack>
                  </article>
                ))}
              </VStack>
            ))}
        </VStack>
      </section>
      {settingsModal}
    </main>
  );
}
